import { cn } from "@/lib/utils";

export type OwnerUser = {
  id: string;
  name?: string | null;
  email?: string | null;
};

/**
 * Fixed palette for owner circles. Each user keeps the same colour everywhere
 * because it is derived from their (stable) id.
 */
const AVATAR_COLORS = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-lime-600",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-cyan-600",
  "bg-sky-500",
  "bg-blue-600",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-stone-500",
] as const;

export function userColorClass(seed: string | null | undefined): string {
  if (!seed) return "bg-muted-foreground";
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]!;
}

/** Two letters: first + last name initials, else the first two email letters. */
export function userInitials(
  name: string | null | undefined,
  email: string | null | undefined
): string {
  const cleanName = (name ?? "").trim();
  if (cleanName) {
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
    }
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  const local = (email ?? "").split("@")[0] ?? "";
  const words = local.split(/[._-]+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  if (local) return local.slice(0, 2).toUpperCase();
  return "??";
}

export function userLabel(
  name: string | null | undefined,
  email: string | null | undefined
): string {
  return (name ?? "").trim() || (email ?? "").trim() || "Unknown";
}

const SIZE_CLASSES = {
  xs: "h-5 w-5 text-[9px]",
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
} as const;

export function UserAvatar({
  id,
  name,
  email,
  size = "sm",
  className,
  title,
}: {
  id: string | null | undefined;
  name?: string | null;
  email?: string | null;
  size?: keyof typeof SIZE_CLASSES;
  className?: string;
  title?: string;
}) {
  const label = userLabel(name, email);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 select-none items-center justify-center rounded-full font-semibold uppercase leading-none text-white ring-1 ring-inset ring-black/5",
        SIZE_CLASSES[size],
        id ? userColorClass(id) : "bg-zinc-300 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300",
        className
      )}
      title={title ?? (id ? label : "Unassigned")}
      aria-label={id ? `Owner: ${label}` : "No owner"}
    >
      {id ? userInitials(name, email) : "—"}
    </span>
  );
}
